import { Component, HostListener, OnInit } from '@angular/core';
import { Session } from '@models/Sessions/Session';
import { Edge, Vertex, Workflow } from '@models/Sessions/Workflow';
import { ConfigService } from '@services/config.service';
import { SessionWorkflowService } from '@services/sessions/session.workflow.service';
import { SessionsRequestService } from '@services/sessions/sessions.request.service';

@Component({
  selector: 'app-session-workflow',
  templateUrl: './session-workflow.component.html',
  styleUrls: ['./session-workflow.component.scss'],
})
export class SessionWorkflowComponent implements OnInit {
  mouse: { x: number; y: number } = { x: 0, y: 0 };

  sessions: Session[] = [];
  workflow: Workflow = {
    vertices: [],
    edges: [],
  };

  freeEdge: Edge = undefined;

  addSessionOpened = false;

  constructor(
    private sessionsService: SessionsRequestService,
    private workflowService: SessionWorkflowService,
    public config: ConfigService,
  ) {}

  ngOnInit() {
    this.sessionsService.getAdminAll().subscribe(data => {
      this.sessions = data.filter(s => !s.config.cluster);
      this.workflowService.load().subscribe(_data => {
        if (_data) {
          this.load(_data);
        }
      });
    });
  }

  get addableSessions() {
    return this.sessions.filter(s => !this.isAdded(s));
  }

  openAddSession() {
    this.addSessionOpened = true;
  }

  closeAddSession() {
    this.addSessionOpened = false;
  }

  addSession(session: Session) {
    if (!this.isAdded(session)) {
      this.workflow.vertices.push({
        ref: session,
        x: 0,
        y: 0,
      });
      this.closeAddSession();
    }
  }

  removeSession(session: Session) {
    this.workflow.vertices = this.workflow.vertices.filter(
      v => v.ref !== session,
    );
    this.workflow.edges = this.workflow.edges.filter(
      e => e.from.ref !== session && e.to.ref !== session,
    );
  }

  isAdded(session: Session) {
    return this.workflow.vertices.some(v => v.ref === session);
  }

  vertexMoved(vertex: Vertex, event) {
    vertex.x = event.x;
    vertex.y = event.y;
  }

  createEdge(vertex: Vertex) {
    if (!this.freeEdge) {
      this.freeEdge = {
        from: vertex,
        to: undefined,
      };
    }
  }

  endEdge(vertex: Vertex) {
    if (
      this.freeEdge &&
      vertex !== this.freeEdge.from &&
      !this.workflow.edges.some(
        e => e.from === this.freeEdge.from && e.to === vertex,
      )
    ) {
      this.freeEdge.to = vertex;
      this.workflow.edges.push(this.freeEdge);

      if (
        this.workflow.edges.some(
          e => e.from === this.freeEdge.to && e.to === this.freeEdge.from,
        )
      ) {
        this.freeEdge.return = true;
      }

      this.freeEdge = undefined;
    }
  }

  removeEdge(edge: Edge) {
    this.workflow.edges = this.workflow.edges.filter(e => e !== edge);
  }

  edgeTransform(edge: Edge) {
    let from = { x: edge.from.x + 224, y: edge.from.y + 36 };
    let to = { x: 0, y: 0 };

    if (edge.to) {
      to = { x: edge.to.x + 32, y: edge.to.y + 36 };
    } else {
      to = { x: this.mouse.x - 150, y: this.mouse.y - 150 };
    }

    if (edge.return) {
      from = { x: edge.from.x + 32, y: edge.from.y + 36 };
      to = { x: edge.to.x + 224, y: edge.to.y + 36 };
    }

    const d = { x: from.x - to.x, y: from.y - to.y };
    const l = Math.sqrt(d.x * d.x + d.y * d.y);
    const a = (Math.atan2(-d.y, -d.x) * 180) / Math.PI;

    return `translate(${from.x}px, ${from.y}px) rotate(${a}deg) scaleX(${l / 100})`;
  }

  edgeChevronTransform(edge: Edge) {
    let from = { x: edge.from.x + 224, y: edge.from.y + 36 };
    let to = { x: 0, y: 0 };

    if (edge.to) {
      to = { x: edge.to.x + 32, y: edge.to.y + 36 };
    } else {
      to = { x: this.mouse.x - 150, y: this.mouse.y - 150 };
    }

    if (edge.return) {
      from = { x: edge.from.x + 32, y: edge.from.y + 36 };
      to = { x: edge.to.x + 224, y: edge.to.y + 36 };
    }

    const d = { x: from.x - to.x, y: from.y - to.y };
    const l = Math.sqrt(d.x * d.x + d.y * d.y);

    return `translateX(${(8 * 100) / l}px) scaleX(${100 / l})`;
  }

  @HostListener('document:mousemove', ['$event'])
  updateMouse(event) {
    this.mouse.x = event.clientX;
    this.mouse.y = event.clientY;
  }

  @HostListener('document:keydown.escape', [])
  discardFreeEdge() {
    this.freeEdge = undefined;
  }

  save() {
    const formatted = {
      vertices: this.workflow.vertices.map(v => ({
        x: v.x,
        y: v.y,
        session_id: v.ref._id,
      })),
      edges: this.workflow.edges.map(e => ({
        from: e.from.ref._id,
        to: e.to.ref._id,
        return: e.return,
      })),
    };

    this.workflowService.save(formatted).subscribe(() => {
      window.alert('Workflow Saved!');
    });
  }

  load(formatted) {
    this.workflow.vertices = formatted.vertices.map(v => ({
      x: v.x,
      y: v.y,
      ref: this.sessions.find(
        s => s._id.toString() === v.session_id.toString(),
      ),
    }));

    this.workflow.edges = formatted.edges.map(e => ({
      from: this.workflow.vertices.find(
        v => v.ref._id.toString() === e.from.toString(),
      ),
      to: this.workflow.vertices.find(
        v => v.ref._id.toString() === e.to.toString(),
      ),
      return: e.return,
    }));
  }
}
